import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api, { getStorefrontApiPrefix } from '../services/api';
import { CartItem, CartTotals, ApiResponse } from '../types';

// ---------------------------------------------------------------------------
// CartSlice
//
// Manages the guest/authenticated shopping cart on the customer side.
//
// Token persistence:
//   Cart token is stored in localStorage ('recurr_cart_token') so it survives
//   page reloads. On app boot, the token is loaded from localStorage and the
//   cart is fetched from the API. If the API returns 404 (expired/invalid),
//   the stored token is cleared.
//
// Optimistic updates:
//   addItem, removeItem, updateItem update Redux state immediately then
//   fire the API call. On API error the store is rolled back.
//   This keeps the UI snappy without waiting for network round-trips.
// ---------------------------------------------------------------------------

const CART_TOKEN_KEY = 'recurr_cart_token';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface CartState {
  token: string | null;
  items: CartItem[];
  totals: CartTotals | null;
  loading: boolean;
  error: string | null;
  drawerOpen: boolean;
}

const initialState: CartState = {
  token:      localStorage.getItem(CART_TOKEN_KEY),
  items:      [],
  totals:     null,
  loading:    false,
  error:      null,
  drawerOpen: false,
};

// ---------------------------------------------------------------------------
// API response shapes
// ---------------------------------------------------------------------------

interface CreateCartResponse {
  cartId: string;
  expiresAt: string;
}

interface CartWithItemsResponse {
  session: {
    id: string;
    sessionToken: string;
    expiresAt: string;
  };
  items: Array<{
    id: string;
    productId: string;
    billingCycle: string;
    quantity: number;
    unitPrice: number;
    setupFee: number;
  }>;
  totals: CartTotals;
}

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Creates a new guest cart and stores the token.
 * Called before adding the first item if no token exists.
 */
export const initCart = createAsyncThunk<string, void, { rejectValue: string }>(
  'cart/init',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<CreateCartResponse>>(`${getStorefrontApiPrefix()}/cart`);
      const token = response.headers['x-cart-token'] as string;
      if (!token) throw new Error('No cart token in response');
      localStorage.setItem(CART_TOKEN_KEY, token);
      return token;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error?.message || 'Failed to create cart');
    }
  }
);

/**
 * Fetches the current cart from the API using the stored token.
 * Used on app boot to restore cart state.
 */
export const fetchCart = createAsyncThunk<CartWithItemsResponse, void, { rejectValue: string }>(
  'cart/fetch',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { cart: CartState };
    const token = state.cart.token;

    if (!token) return rejectWithValue('No cart token');

    try {
      const response = await api.get<ApiResponse<CartWithItemsResponse>>(`${getStorefrontApiPrefix()}/cart`, {
        headers: { 'X-Cart-Token': token },
      });
      return response.data.data as CartWithItemsResponse;
    } catch (err: any) {
      if (err.response?.status === 404 || err.response?.status === 403) {
        // Cart expired or invalid — clear the token
        localStorage.removeItem(CART_TOKEN_KEY);
      }
      return rejectWithValue(err.response?.data?.error?.message || 'Failed to fetch cart');
    }
  }
);

/**
 * Adds an item to the cart. Creates the cart first if no token exists.
 */
export const addCartItemAsync = createAsyncThunk<
  CartItem,
  {
    productId: string;
    billingCycle: string;
    quantity?: number;
    // Wave 4.6 — configurable option selections
    selectedOptions?: { optionId: string; quantity?: number }[];
    // Wave 4.6 — add-on lines carry their parent product id
    parentProductId?: string;
  },
  { rejectValue: string; state: { cart: CartState } }
>(
  'cart/addItem',
  async (data, { getState, dispatch, rejectWithValue }) => {
    let state = getState();
    let token = state.cart.token;

    // Create cart on demand if we don't have a token yet
    if (!token) {
      const result = await dispatch(initCart());
      if (initCart.rejected.match(result)) {
        return rejectWithValue('Failed to create cart');
      }
      token = result.payload as string;
    }

    try {
      const response = await api.post<ApiResponse<any>>(`${getStorefrontApiPrefix()}/cart/items`, data, {
        headers: { 'X-Cart-Token': token },
      });
      // Refresh items + totals (the add response is a single item; totals are
      // only computed server-side by getCart).
      await dispatch(fetchCart());
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error?.message || 'Failed to add item');
    }
  }
);

/**
 * Updates quantity or billing cycle for an existing cart item.
 */
export const updateCartItemAsync = createAsyncThunk<
  CartItem,
  { itemId: string; quantity?: number; billingCycle?: string },
  { rejectValue: string; state: { cart: CartState } }
>(
  'cart/updateItem',
  async ({ itemId, ...data }, { getState, dispatch, rejectWithValue }) => {
    const token = getState().cart.token;
    if (!token) return rejectWithValue('No cart token');

    try {
      const response = await api.put<ApiResponse<any>>(`${getStorefrontApiPrefix()}/cart/items/${itemId}`, data, {
        headers: { 'X-Cart-Token': token },
      });
      await dispatch(fetchCart());   // refresh totals after a cycle/qty change
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error?.message || 'Failed to update item');
    }
  }
);

/**
 * Removes a line item from the cart.
 */
export const removeCartItemAsync = createAsyncThunk<
  string,
  string,
  { rejectValue: string; state: { cart: CartState } }
>(
  'cart/removeItem',
  async (itemId, { getState, dispatch, rejectWithValue }) => {
    const token = getState().cart.token;
    if (!token) return rejectWithValue('No cart token');

    try {
      await api.delete(`${getStorefrontApiPrefix()}/cart/items/${itemId}`, {
        headers: { 'X-Cart-Token': token },
      });
      await dispatch(fetchCart());   // refresh totals after removal
      return itemId;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error?.message || 'Failed to remove item');
    }
  }
);

// ---------------------------------------------------------------------------
// Helper: map API cart item row to CartItem type
// ---------------------------------------------------------------------------
function mapApiItem(raw: any): CartItem {
  return {
    id:           raw.id,
    productId:    raw.productId || raw.product_id,
    productName:  raw.productName || raw.product_name || '',
    // 'hosting' until 2026-08-18 — a fallback that is not a member of
    // CatalogProductType at all, so a row arriving without a type was labelled
    // with a value no consumer can match (the quantity stepper reads it). The
    // §5.2 re-cut replaced the hosting-era vocabulary with
    // simple/variable/grouped/external; 'simple' is the neutral member.
    productType:  raw.productType || raw.product_type || 'simple',
    billingCycle: raw.billingCycle || raw.billing_cycle,
    quantity:     raw.quantity,
    unitPrice:    raw.unitPrice ?? raw.unit_price ?? 0,
    setupFee:     raw.setupFee  ?? raw.setup_fee  ?? 0,
    configOptions: raw.configOptions || raw.config_options || undefined,
    availableBillingCycles: raw.availableCycles || raw.available_cycles || undefined,
  };
}

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    /** Store a cart token (e.g. after createCart API call). */
    setToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
      localStorage.setItem(CART_TOKEN_KEY, action.payload);
    },

    /** Replace the full cart state from an API response. */
    setCart(state, action: PayloadAction<{ items: CartItem[]; totals: CartTotals }>) {
      state.items  = action.payload.items;
      state.totals = action.payload.totals;
    },

    /** Open the cart drawer. */
    openDrawer(state) {
      state.drawerOpen = true;
    },

    /** Close the cart drawer. */
    closeDrawer(state) {
      state.drawerOpen = false;
    },

    /** Toggle the cart drawer open/closed. */
    toggleDrawer(state) {
      state.drawerOpen = !state.drawerOpen;
    },

    /** Clear all cart state (called after successful order). */
    clearCart(state) {
      state.token     = null;
      state.items     = [];
      state.totals    = null;
      state.error     = null;
      state.drawerOpen = false;
      localStorage.removeItem(CART_TOKEN_KEY);
    },

    /** Clear any error message. */
    clearError(state) {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    // ---- initCart ----------------------------------------------------------
    builder
      .addCase(initCart.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(initCart.fulfilled, (state, action) => {
        state.loading = false;
        state.token   = action.payload;
      })
      .addCase(initCart.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload ?? 'Failed to create cart';
      });

    // ---- fetchCart ---------------------------------------------------------
    builder
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items   = (action.payload.items || []).map(mapApiItem);
        state.totals  = action.payload.totals;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        // If the cart is expired/invalid, clear the token too
        if (
          action.payload === 'No cart token' ||
          action.payload?.includes('not found') ||
          action.payload?.includes('expired')
        ) {
          state.token = null;
        }
        // Don't set error for the "no token" case — it's expected on first visit
        if (action.payload !== 'No cart token') {
          state.error = action.payload ?? 'Failed to fetch cart';
        }
      });

    // ---- addCartItemAsync --------------------------------------------------
    builder
      .addCase(addCartItemAsync.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(addCartItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        const raw = action.payload;
        // Check if item already in state (upsert from API)
        const existingIdx = state.items.findIndex(i => i.id === raw.id);
        if (existingIdx >= 0) {
          state.items[existingIdx] = mapApiItem(raw);
        } else {
          state.items.push(mapApiItem(raw));
        }
      })
      .addCase(addCartItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload ?? 'Failed to add item';
      });

    // ---- updateCartItemAsync -----------------------------------------------
    builder
      .addCase(updateCartItemAsync.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(updateCartItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        const updated = mapApiItem(action.payload);
        const idx = state.items.findIndex(i => i.id === updated.id);
        if (idx >= 0) state.items[idx] = updated;
      })
      .addCase(updateCartItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload ?? 'Failed to update item';
      });

    // ---- removeCartItemAsync -----------------------------------------------
    builder
      .addCase(removeCartItemAsync.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(removeCartItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items   = state.items.filter(i => i.id !== action.payload);
      })
      .addCase(removeCartItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload ?? 'Failed to remove item';
      });
  },
});

export const {
  setToken,
  setCart,
  openDrawer,
  closeDrawer,
  toggleDrawer,
  clearCart,
  clearError,
} = cartSlice.actions;

export default cartSlice.reducer;
